import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'business';
  noIndex?: boolean;
  schema?: Record<string, unknown>;
  lang?: string;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://unqcode.com';
const DEFAULT_OG_IMAGE = 'https://public.readdy.ai/ai/img_res/1ea038cc2baa8b83f8455884dd010764.jpg';

// Helper: inject or update a single ld+json script by a unique id
const upsertSchemaScript = (id: string, data: Record<string, unknown>) => {
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
};

// Helper: remove all schema scripts injected by this component
const removeSchemaScripts = (ids: string[]) => {
  ids.forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.remove();
  });
};

export const SEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  ogType = 'website',
  noIndex = false,
  schema,
  lang,
}: SEOProps) => {
  const { i18n } = useTranslation();
  const currentLang = lang || i18n.language || 'en';

  useEffect(() => {
    // Update document title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMeta = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Update lang attribute
    document.documentElement.lang = currentLang;

    // Basic meta tags
    if (description) {
      updateMeta('description', description);
    }
    if (keywords) {
      updateMeta('keywords', keywords);
    }

    // Open Graph
    updateMeta('og:title', title || document.title, true);
    updateMeta('og:description', description || '', true);
    updateMeta('og:image', ogImage || DEFAULT_OG_IMAGE, true);
    updateMeta('og:type', ogType, true);
    updateMeta('og:url', canonical || window.location.href, true);
    updateMeta('og:locale', currentLang === 'ar' ? 'ar_SA' : 'en_US', true);

    // Twitter Card
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', title || document.title);
    updateMeta('twitter:description', description || '');
    updateMeta('twitter:image', ogImage || DEFAULT_OG_IMAGE);

    // Robots
    if (noIndex) {
      updateMeta('robots', 'noindex, nofollow');
    } else {
      updateMeta('robots', 'index, follow');
    }

    // Canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical || window.location.href;

    // Last modified
    updateMeta('last-modified', new Date().toISOString());

    // Geo tags for Saudi Arabia
    updateMeta('geo.region', 'SA');
    updateMeta('geo.placename', 'Riyadh');
    updateMeta('geo.position', '24.7136;46.6753');
    updateMeta('ICBM', '24.7136, 46.6753');

    // Schema.org JSON-LD — support @graph (multiple schemas) or single schema
    const injectedIds: string[] = [];

    if (schema) {
      const graphItems: Record<string, unknown>[] =
        schema['@graph'] && Array.isArray(schema['@graph'])
          ? (schema['@graph'] as Record<string, unknown>[])
          : [schema];

      graphItems.forEach((item, index) => {
        const schemaType = (item['@type'] as string) || 'Schema';
        const scriptId = `schema-org-${schemaType.toLowerCase().replace(/\s+/g, '-')}-${index}`;
        injectedIds.push(scriptId);
        upsertSchemaScript(scriptId, {
          '@context': 'https://schema.org',
          ...item,
        });
      });

      // Store injected ids for cleanup
      (window as unknown as Record<string, unknown>).__seoSchemaIds = injectedIds;
    }

    return () => {
      // Cleanup all injected schema scripts on unmount
      const ids =
        ((window as unknown as Record<string, unknown>).__seoSchemaIds as string[]) || injectedIds;
      removeSchemaScripts(ids);
      delete (window as unknown as Record<string, unknown>).__seoSchemaIds;
    };
  }, [title, description, keywords, canonical, ogImage, ogType, noIndex, schema, currentLang]);

  return null;
};

// Predefined schemas
export const createOrganizationSchema = () => ({
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: 'Unique Code',
  alternateName: 'الكود المميز',
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/logo-dark.png`,
    width: 200,
    height: 60,
  },
  email: 'Info@unqcode.com',
  telephone: '+966552099231',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Riyadh',
    addressRegion: 'Riyadh Region',
    addressCountry: 'SA',
  },
  sameAs: [
    'https://twitter.com/unqcode',
    'https://linkedin.com/company/unqcode',
    'https://instagram.com/unqcode',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+966552099231',
    contactType: 'customer service',
    availableLanguage: ['Arabic', 'English'],
  },
});

export const createWebSiteSchema = () => ({
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: 'Unique Code',
  alternateName: 'الكود المميز',
  url: SITE_URL,
  publisher: {
    '@id': `${SITE_URL}/#organization`,
  },
  inLanguage: ['en', 'ar'],
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/services?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

export const createLocalBusinessSchema = () => ({
  '@type': 'ProfessionalService',
  '@id': `${SITE_URL}/#localbusiness`,
  name: 'Unique Code',
  alternateName: 'الكود المميز',
  url: SITE_URL,
  logo: `${SITE_URL}/logo-dark.png`,
  image: `${SITE_URL}/logo-dark.png`,
  email: 'Info@unqcode.com',
  telephone: '+966552099231',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Riyadh',
    addressLocality: 'Riyadh',
    addressRegion: 'Riyadh Region',
    addressCountry: 'SA',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 24.7136,
    longitude: 46.6753,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'],
      opens: '09:00',
      closes: '18:00',
    },
  ],
  priceRange: '$$$',
  currenciesAccepted: 'SAR, USD',
  paymentAccepted: 'Cash, Credit Card, Bank Transfer',
  areaServed: [
    { '@type': 'Country', name: 'Saudi Arabia' },
    { '@type': 'Country', name: 'United Arab Emirates' },
    { '@type': 'AdministrativeArea', name: 'GCC' },
  ],
});

export const createWebPageSchema = (title: string, description: string, url: string) => ({
  '@type': 'WebPage',
  '@id': `${SITE_URL}${url}#webpage`,
  name: title,
  description,
  url: `${SITE_URL}${url}`,
  isPartOf: {
    '@id': `${SITE_URL}/#website`,
  },
  about: {
    '@id': `${SITE_URL}/#organization`,
  },
  datePublished: '2024-01-01',
  dateModified: new Date().toISOString().split('T')[0],
  inLanguage: ['en', 'ar'],
});

export const createServiceSchema = (serviceName: string, description: string, url: string) => ({
  '@type': 'Service',
  serviceType: serviceName,
  provider: {
    '@id': `${SITE_URL}/#organization`,
  },
  description,
  url: `${SITE_URL}${url}`,
  areaServed: {
    '@type': 'Country',
    name: 'Saudi Arabia',
  },
});

export const createFAQPageSchema = (faqs: { question: string; answer: string }[]) => ({
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export const createJobPostingSchema = (jobs: { title: string; description: string; employmentType?: string; workLocation?: string }[]) => ({
  '@type': 'ItemList',
  name: 'Unique Code Job Openings',
  url: `${SITE_URL}/join`,
  itemListElement: jobs.map((job, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'JobPosting',
      title: job.title,
      description: job.description,
      employmentType: job.employmentType || 'FULL_TIME',
      jobLocationType: job.workLocation || 'TELECOMMUTE',
      hiringOrganization: {
        '@id': `${SITE_URL}/#organization`,
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Riyadh',
          addressCountry: 'SA',
        },
      },
      datePosted: '2026-01-01',
      validThrough: '2026-12-31',
    },
  })),
});

export const createContactPageSchema = () => ({
  '@type': 'ContactPage',
  '@id': `${SITE_URL}/contact#webpage`,
  name: 'Contact Unique Code',
  url: `${SITE_URL}/contact`,
  mainEntity: {
    '@id': `${SITE_URL}/#organization`,
  },
});

export const createBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${SITE_URL}${item.url}`,
  })),
});

export default SEO;